import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ChevronDown, Gem, BedDouble, Users, Moon } from 'lucide-react';

const faqs = [
  {
    q: "What is a cave stay in Jaisalmer?",
    a: "A cave stay is an accommodation built into or designed to evoke carved rock — walls of natural stone, low lighting, and an atmosphere that's wholly unlike a conventional hotel room. Rudragarh's Catacomb Suite is the only one of its kind in this stretch of Rajasthan: a subterranean carved-cave cottage with natural climate control, lantern-lit interiors, and a private desert ambience that guests call transformative. It's designed for those who want something to remember long after the trip."
  },
  {
    q: "What's included in the Catacomb Suite at Rudragarh?",
    a: "The Catacomb Suite includes 2 double beds (sleeping up to 4 guests), a private attached bathroom, minibar, and full access to all 18 resort facilities — swimming pool, gym, dining hall, bar, private sand dunes, cultural programs, and all adventure activities. The suite is part of the broader Rudragarh property, so guests get the cave experience alongside everything a boutique desert resort offers."
  },
  {
    q: "How many guests can the cave stay accommodate?",
    a: "The Catacomb Suite sleeps up to 4 guests across 2 double beds. Each unit has its own private attached bathroom. It's ideal for two couples, a small family, or a group of four friends wanting a genuinely unique desert experience. There are only two Catacomb Suite units on the property — so availability is limited and advance booking is recommended."
  }
];

const details = [
  { icon: <BedDouble size={28} />, title: "2 Double Beds", desc: "Carved into the cave layout — both beds have full privacy and natural stone walls on three sides." },
  { icon: <Users size={28} />, title: "Up to 4 Guests", desc: "One attached bathroom serves all four guests. Ideal for couples travelling together or a small family." },
  { icon: <Moon size={28} />, title: "Night Ambience", desc: "Lanterns, rock walls, and total darkness outside — the cave at night is the experience guests return for." },
  { icon: <Gem size={28} />, title: "One Unit Only", desc: "There is exactly one cave stay at Rudragarh. Reserve early — it fills up fast, especially in peak season." }
];

const CaveStay = () => {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="min-h-screen pt-20">
      <Helmet>
        <title>Cave Stay in Jaisalmer | The Signature Cave Room at Rudragarh</title>
        <meta name="description" content="Experience Jaisalmer's most unique stay — Rudragarh's Signature Cave Room. Carved rock walls, 2 double beds, private desert ambience near Sam Sand Dunes." />
        <meta property="og:title" content="Cave Stay in Jaisalmer | The Signature Cave Room at Rudragarh" />
        <meta property="og:description" content="Rudragarh's The Catacomb Suite — carved rock walls, lantern light, 2 double beds, 4 guests, and the desert all around. Near Sam Sand Dunes, Jaisalmer." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rudragarh.com/jaisalmer/cave-stay" />
        <meta property="og:image" content="https://www.rudragarh.com/images/resort-cave.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Cave Stay Jaisalmer | Rudragarh Signature Cave Room" />
        <meta name="twitter:description" content="Jaisalmer's most unique stay — carved rock walls, 2 double beds, lantern light, and private dunes at Rudragarh Resort." />
        <meta name="twitter:image" content="https://www.rudragarh.com/images/resort-cave.jpg" />
        <link rel="canonical" href="https://www.rudragarh.com/jaisalmer/cave-stay" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a }
          }))
        })}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative h-[60vh] flex items-center justify-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('/images/resort-cave.jpg')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-void/80 via-void/55 to-void" />
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <span className="overline">The Signature Experience</span>
            <h1 className="text-4xl md:text-6xl font-heading font-medium mb-4">
              Jaisalmer's Most <span className="text-gold-500">Unique Stay</span>
            </h1>
            <p className="text-foreground/70 max-w-2xl mx-auto text-lg">
              Inside a cave. Carved rock. Lantern light. The desert outside. There is nothing else like this in Rajasthan.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Intro + Book CTA */}
      <section className="section-padding bg-void-paper">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <span className="overline">The Cave Room</span>
              <h2 className="text-3xl md:text-4xl font-heading font-medium">
                Inside the <span className="text-gold-500">The Catacomb Suite</span>
              </h2>
              <p className="text-foreground/70 leading-relaxed">
                There are cave-inspired rooms, and there is the Rudragarh The Catacomb Suite. The difference is in the material — actual carved rock walls, not tiled imitations. The room sits within the Rudragarh property near Sam Sand Dunes, and the construction honours the desert around it: raw stone, warm lanterns, and a silence that only the Thar produces.
              </p>
              <p className="text-foreground/70 leading-relaxed">
                Two double beds are set into the cave layout. The single attached bathroom is finished to full resort standard. Up to four guests share the space — it's large enough for two couples or a small family, intimate enough to feel like a private world.
              </p>
              <p className="text-foreground/70 leading-relaxed">
                There is only one cave stay on the property. Once it's booked, it's gone. Advance reservation is strongly recommended, particularly October through March.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-2">
                <Link to="/stay/catacomb-suite" className="btn-primary">View Room Details</Link>
                <Link to="/book" className="btn-outline">Book Now</Link>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-4"
            >
              <div className="bg-void border border-gold-500/20 p-8 space-y-6">
                <div className="flex items-center justify-between pb-4 border-b border-gold-500/10">
                  <span className="font-heading text-2xl text-gold-500">₹10,000</span>
                  <span className="text-foreground/40 text-sm">per night</span>
                </div>
                {[
                  { label: "Room type", value: "Cave — carved rock construction" },
                  { label: "Beds", value: "2 double beds" },
                  { label: "Guests", value: "Up to 4" },
                  { label: "Bathroom", value: "1 attached (shared by all guests)" },
                  { label: "Minibar", value: "Included" },
                  { label: "Units available", value: "1 only — book early" },
                  { label: "Check-in", value: "11:00 AM" },
                  { label: "Check-out", value: "10:00 AM" }
                ].map((item, i) => (
                  <div key={i} className="flex items-start justify-between gap-4">
                    <span className="text-foreground/50 text-sm">{item.label}</span>
                    <span className="text-foreground/90 text-sm text-right">{item.value}</span>
                  </div>
                ))}
                <Link
                  to="/book"
                  className="block w-full text-center bg-gold-500 text-black px-8 py-4 uppercase tracking-widest text-sm hover:bg-gold-400 transition-colors mt-4"
                >
                  Book the Cave Stay
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Room Details Grid */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="text-center mb-14">
            <span className="overline">What to Expect</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              Inside the <span className="text-gold-500">Cave</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {details.map((d, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-void-subtle border border-gold-500/10 p-8 space-y-4"
              >
                <div className="text-gold-500">{d.icon}</div>
                <h3 className="font-heading text-xl">{d.title}</h3>
                <p className="text-foreground/60 text-sm leading-relaxed">{d.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Also Consider */}
      <section className="section-padding bg-void-paper">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="text-center mb-14">
            <span className="overline">Other Stays</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              More at <span className="text-gold-500">Rudragarh</span>
            </h2>
            <p className="text-foreground/60 mt-4 max-w-xl mx-auto">
              If the cave stay is booked, these rooms share the same private dunes and resort facilities.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {[
              { slug: "heritage-huts", name: "The Heritage Huts", price: 15000, guests: 4, category: "unique", desc: "Two levels of bamboo construction — the second most distinctive stay on the property." },
              { slug: "grand-quad", name: "The Grand Quad", price: 7000, guests: 4, category: "modern", desc: "The property's largest suite — king bed, modern finishes, desert views." }
            ].map((room, i) => (
              <motion.div
                key={room.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-void-subtle border border-gold-500/10 group"
              >
                <Link to={`/stay/${room.slug}`}>
                  <div className="p-6 space-y-3">
                    <span className="text-gold-500 text-xs uppercase tracking-widest">{room.category}</span>
                    <h3 className="font-heading text-xl group-hover:text-gold-500 transition-colors">{room.name}</h3>
                    <p className="text-foreground/60 text-sm leading-relaxed">{room.desc}</p>
                    <div className="flex items-center justify-between pt-4 border-t border-gold-500/10">
                      <span className="text-gold-500 font-heading text-lg">₹{room.price.toLocaleString()}<span className="text-foreground/40 text-sm font-normal">/night</span></span>
                      <span className="text-foreground/40 text-sm">Up to {room.guests} guests</span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding">
        <div className="max-w-3xl mx-auto container-padding">
          <div className="text-center mb-12">
            <span className="overline">Questions</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              About the <span className="text-gold-500">Cave Stay</span>
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-void-subtle border border-gold-500/10">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-6 text-left"
                >
                  <span className="font-heading text-lg pr-4">{faq.q}</span>
                  <ChevronDown
                    size={20}
                    className={`text-gold-500 flex-shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`}
                  />
                </button>
                {openFaq === i && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="px-6 pb-6"
                  >
                    <p className="text-foreground/70 leading-relaxed">{faq.a}</p>
                  </motion.div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('/images/resort-cave.jpg')` }}
        />
        <div className="absolute inset-0 bg-void/85" />
        <div className="relative z-10 max-w-3xl mx-auto text-center px-6">
          <span className="overline">One Room Only</span>
          <h2 className="text-3xl md:text-5xl font-heading font-medium mb-6">
            Don't Miss the <span className="text-gold-500">Cave Stay</span>
          </h2>
          <p className="text-foreground/70 mb-10 max-w-xl mx-auto">
            There is only one The Catacomb Suite at Rudragarh. Check availability now — peak season books months in advance.
          </p>
          <Link to="/book" className="bg-gold-500 text-black px-8 py-4 uppercase tracking-widest text-sm hover:bg-gold-400 transition-colors inline-block">
            Book the Cave Stay
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CaveStay;
